import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { motion } from 'framer-motion';
import { Section } from '../../stores/builderStore';
import { useBuilderStore } from '../../stores/builderStore';
import {
  GripVertical,
  Eye,
  EyeOff,
  Copy,
  Trash2,
  ChevronUp,
  ChevronDown,
  Settings,
  Image,
  Video
} from 'lucide-react';
import { NetflixHero } from './PremiumSections/NetflixHero';
import { AppStoreHero } from './PremiumSections/AppStoreHero';
import { PremiumCards } from './PremiumSections/PremiumCards';
import PremiumContent from './PremiumSections/PremiumContent';
import PremiumButtonGrid from './PremiumSections/PremiumButtonGrid';
import TextCard from './PremiumSections/TextCard';

interface SortableSectionProps {
  section: Section;
  index: number;
  isSelected: boolean;
  onSelect: () => void;
  device: 'desktop' | 'tablet' | 'mobile';
}

export const SortableSection: React.FC<SortableSectionProps> = ({
  section,
  index,
  isSelected,
  onSelect,
  device
}) => {
  const {
    deleteSection,
    duplicateSection,
    toggleSectionVisibility,
    moveSection
  } = useBuilderStore();

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: section.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const handleMoveUp = () => {
    if (index > 0) {
      moveSection(index, index - 1);
    }
  };

  const handleMoveDown = () => {
    const sections = useBuilderStore.getState().sections;
    if (index < sections.length - 1) {
      moveSection(index, index + 1);
    }
  };

  // Helper function to convert styles object to CSS properties
  const getStyleFromValues = (styles: any) => {
    if (!styles) return {};

    return {
      backgroundColor: styles.backgroundColor,
      color: styles.textColor,
      // Padding
      paddingTop: styles.paddingTop ? `${styles.paddingTop}px` : undefined,
      paddingBottom: styles.paddingBottom ? `${styles.paddingBottom}px` : undefined,
      paddingLeft: styles.paddingLeft ? `${styles.paddingLeft}px` : undefined,
      paddingRight: styles.paddingRight ? `${styles.paddingRight}px` : undefined,
      // Margin
      marginTop: styles.marginTop ? `${styles.marginTop}px` : undefined,
      marginBottom: styles.marginBottom ? `${styles.marginBottom}px` : undefined,
      marginLeft: styles.marginLeft ? `${styles.marginLeft}px` : undefined,
      marginRight: styles.marginRight ? `${styles.marginRight}px` : undefined,
      // Typography
      fontSize: styles.fontSize,
      fontWeight: styles.fontWeight,
      lineHeight: styles.lineHeight,
      letterSpacing: styles.letterSpacing,
      // Effects
      borderRadius: styles.borderRadius ? `${styles.borderRadius}px` : undefined,
      boxShadow: styles.boxShadow,
      opacity: styles.opacity,
      // Background Image
      backgroundImage: styles.backgroundImage ? `url(${styles.backgroundImage})` : undefined,
      backgroundSize: styles.backgroundSize,
      backgroundPosition: styles.backgroundPosition,
      backgroundRepeat: styles.backgroundRepeat,
      // Border
      borderWidth: styles.borderWidth ? `${styles.borderWidth}px` : undefined,
      borderColor: styles.borderColor,
      borderStyle: styles.borderStyle || (styles.borderWidth ? 'solid' : undefined),
      // Gap for flex/grid containers
      gap: styles.gap ? `${styles.gap}px` : undefined,
    };
  };

  // Render section content based on type
  const renderSectionContent = () => {
    // Ensure type field exists (fallback to section_type)
    const type = section.type || (section as any).section_type || '';
    const props = section.props || {};
    const styles = section.styles || {};
    const variant = props.variant || 'default';

    // Debug logging
    console.log('SortableSection rendering:', {
      id: section.id,
      type: type,
      section_type: (section as any).section_type,
      title: section.title,
      props: props,
      hasType: !!type,
      propsKeys: Object.keys(props)
    });

    switch (type) {
      case 'hero':
      case 'hero_center':
      case 'hero_banner': // Map database type to hero rendering
        console.log('Rendering hero with variant:', variant, 'props:', props);

        // Render premium hero variants
        if (variant === 'netflix') {
          return (
            <NetflixHero
              title={props.title}
              subtitle={props.subtitle}
              description={props.description}
              backgroundImage={props.backgroundImage}
              videoUrl={props.videoUrl}
              buttonText={props.buttonText}
              buttonLink={props.buttonLink}
              height={props.height}
            />
          );
        }

        if (variant === 'appstore') {
          return (
            <AppStoreHero
              category={props.category}
              title={props.title}
              subtitle={props.subtitle}
              description={props.description}
              backgroundImage={props.backgroundImage}
              accentColor={props.accentColor}
              buttonText={props.buttonText}
              buttonLink={props.buttonLink}
            />
          );
        }

        // Default hero rendering with enhanced styling
        const textAlign = props.textAlign || 'center';
        const textPosition = props.textPosition || 'center';
        const buttonAlign = props.buttonAlign || textAlign || 'center';
        const overlayIntensity = props.overlayIntensity ?? 0.4;

        // Text alignment classes
        const alignmentClass = {
          left: 'items-start text-left',
          center: 'items-center text-center',
          right: 'items-end text-right'
        }[textAlign] || 'items-center text-center';

        // Vertical position classes
        const positionClass = {
          top: 'justify-start pt-16',
          center: 'justify-center',
          bottom: 'justify-end pb-16'
        }[textPosition] || 'justify-center';

        // Button alignment classes
        const buttonAlignClass = {
          left: 'justify-start',
          center: 'justify-center',
          right: 'justify-end'
        }[buttonAlign] || 'justify-center';

        // Handle link navigation
        const handleButtonClick = (link: string, linkType: string = 'internal') => {
          if (!link) return;
          if (linkType === 'external' || link.startsWith('http://') || link.startsWith('https://')) {
            window.open(link, '_blank', 'noopener,noreferrer');
          } else {
            // For preview, just show alert. In production, use router
            console.log('Navigate to:', link);
          }
        };

        return (
          <motion.div
            className="relative overflow-hidden"
            style={{
              height: props.height || '400px',
              backgroundColor: styles?.backgroundColor || '#1e40af'
            }}
          >
            {props.backgroundImage ? (
              <img
                src={props.backgroundImage}
                alt=""
                className="absolute inset-0 w-full h-full object-cover"
                onError={(e) => console.error('Failed to load image:', props.backgroundImage)}
              />
            ) : (
              <div className="absolute inset-0 bg-gradient-to-br from-blue-600 to-purple-700" />
            )}
            <div
              className="absolute inset-0 bg-black"
              style={{ opacity: overlayIntensity }}
            />
            <div className={`relative h-full flex flex-col ${positionClass} ${alignmentClass} text-white p-8`}>
              <div className="max-w-4xl w-full">
                <h1 className="text-4xl font-bold mb-4 hover:outline hover:outline-2 hover:outline-white/50 cursor-pointer rounded px-2"
                    title="Click section and edit in properties panel">
                  {props.title || 'Hero Title'}
                </h1>
                <p className="text-xl mb-6 hover:outline hover:outline-2 hover:outline-white/50 cursor-pointer rounded px-2"
                   title="Click section and edit in properties panel">
                  {props.subtitle || 'Hero Subtitle'}
                </p>

                {/* Button Container */}
                <div className={`flex gap-4 ${buttonAlignClass}`}>
                  {props.showPrimaryButton !== false && props.buttonText && (
                    <button
                      className="px-6 py-3 bg-white text-blue-600 rounded-lg font-medium hover:bg-gray-100 transition-colors"
                      onClick={() => handleButtonClick(props.buttonLink, props.buttonLinkType)}
                    >
                      {props.buttonText}
                    </button>
                  )}
                  {props.showSecondaryButton && props.secondaryButtonText && (
                    <button
                      className="px-6 py-3 bg-transparent text-white border-2 border-white rounded-lg font-medium hover:bg-white hover:text-blue-600 transition-colors"
                      onClick={() => handleButtonClick(props.secondaryButtonLink, props.secondaryButtonLinkType)}
                    >
                      {props.secondaryButtonText}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </motion.div>
        );

      case 'content':
        // Render Premium Content sections
        return (
          <PremiumContent
            variant={props.variant || 'magazine'}
            title={props.title}
            subtitle={props.subtitle}
            items={props.items || []}
            columns={props.columns}
            backgroundColor={styles.backgroundColor}
            textColor={props.textColor}
            accentColor={props.accentColor}
            titleSize={props.titleSize}
            titleAlign={props.titleAlign}
            contentSize={props.contentSize}
            contentAlign={props.contentAlign}
            lineHeight={props.lineHeight}
            contentWeight={props.contentWeight}
            showDivider={props.showDivider}
            imagePosition={props.imagePosition}
          />
        );

      case 'features':
      case 'feature_cards': // Map database type to features rendering
        // Map items to ensure they have all required fields for premium rendering
        const rawItems = props.items || [
          { icon: '🎯', title: 'Study Materials', description: 'Comprehensive UPSC study resources', link: '#' },
          { icon: '📚', title: 'Mock Tests', description: 'Practice with real exam patterns', link: '#' },
          { icon: '📊', title: 'Analytics', description: 'Track your progress and performance', link: '#' }
        ];

        // Ensure items have proper structure for premium cards
        const featureItems = rawItems.map((item: any, index: number) => ({
          id: item.id || `feature-${index}`,
          title: item.title || `Feature ${index + 1}`,
          subtitle: item.subtitle,
          description: item.description || 'Feature description',
          image: item.image || item.icon, // Use image first, fallback to icon if it's a URL
          link: item.link || '#',
          badge: item.badge,
          rating: item.rating,
          accentColor: item.accentColor || ['#667eea', '#f093fb', '#4facfe', '#fa709a'][index % 4],
          ctaText: item.ctaText,
          overlayIntensity: item.overlayIntensity,
          textPosition: item.textPosition
        }));

        // Use premium cards for features - always enable horizontal scrolling for premium variants
        if (variant === 'netflix' || variant === 'appstore' || variant === 'glass' || variant === 'overlay') {
          console.log('Rendering premium features with variant:', variant, 'items:', featureItems, 'scrollable: true (forced)');
          return (
            <PremiumCards
              title={props.title}
              subtitle={props.subtitle}
              titleAlign={props.titleAlign}
              titleSize={props.titleSize}
              titleWeight={props.titleWeight}
              subtitleSize={props.subtitleSize}
              titleGap={props.titleGap}
              items={featureItems}
              variant={variant as 'netflix' | 'appstore' | 'glass' | 'overlay'}
              columns={props.columns || 3}
              scrollable={true} // Always true for premium variants
              showArrows={true} // Always show arrows for premium
              cardWidth={props.cardWidth || 'medium'}
              gap={props.gap || 24}
              styles={styles} // Pass styles to PremiumCards
            />
          );
        }

        // Default features rendering with enhanced styling
        return (
          <motion.div
            className="p-8"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            {props.title && (
              <motion.h2
                className="text-3xl md:text-4xl font-bold text-center mb-8 heading-premium hover:outline hover:outline-2 hover:outline-gray-400 cursor-pointer rounded px-2 inline-block"
                title="Click section and edit in properties panel"
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
              >
                {props.title}
              </motion.h2>
            )}
            <motion.div
              className={`grid gap-6 ${
              props.columns === 2 ? 'grid-cols-2' :
              props.columns === 4 ? 'grid-cols-2 md:grid-cols-4' :
              'grid-cols-1 md:grid-cols-3'
            }`}>
              {featureItems.map((item: any, i: number) => (
                <motion.a
                  key={i}
                  href={item.link || '#'}
                  className="text-center p-6 rounded-xl bg-white shadow-md hover:shadow-xl transition-all cursor-pointer block transform hover:-translate-y-1"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.1 }}
                  whileHover={{ scale: 1.03 }}
                  onClick={(e) => {
                    // In edit mode, prevent navigation
                    e.preventDefault();
                    console.log('Feature card clicked:', item.title, 'Link:', item.link);
                  }}
                >
                  {item.image && (item.image.startsWith('http') || item.image.startsWith('/')) ? (
                    <img src={item.image} alt={item.title} className="w-16 h-16 mx-auto mb-4 object-contain" />
                  ) : (
                    <div className="text-5xl mb-4">{item.image || item.icon || '⭐'}</div>
                  )}
                  <h3 className="text-xl font-bold mb-2">{item.title || `Feature ${i + 1}`}</h3>
                  <p className="text-gray-600">{item.description || 'Feature description'}</p>
                </motion.a>
              ))}
            </motion.div>
          </motion.div>
        );

      case 'cta':
        return (
          <div
            className="p-12 text-center"
            style={{ backgroundColor: styles?.backgroundColor || '#f3f4f6' }}
          >
            <h2 className="text-3xl font-bold mb-4">{props.title}</h2>
            <p className="text-lg text-gray-600 mb-8">{props.subtitle}</p>
            <button
              className="px-8 py-4 rounded-lg font-medium text-white"
              style={{ backgroundColor: styles?.accentColor || '#3b82f6' }}
            >
              {props.buttonText || 'Get Started'}
            </button>
          </div>
        );

      case 'testimonials_carousel':
      case 'carousel': // Map database type to carousel rendering
        // Check if this is a hero-style carousel with title/subtitle/button
        if (props.title || props.subtitle || props.buttonText) {
          console.log('Rendering carousel as hero with props:', props);
          return (
            <div
              className="relative overflow-hidden"
              style={{
                height: props.height || '400px',
                backgroundColor: styles?.backgroundColor || '#1e40af'
              }}
            >
              {props.backgroundImage ? (
                <img
                  src={props.backgroundImage}
                  alt=""
                  className="absolute inset-0 w-full h-full object-cover"
                  onError={(e) => console.error('Failed to load carousel image:', props.backgroundImage)}
                />
              ) : (
                <div className="absolute inset-0 bg-gradient-to-br from-purple-600 to-pink-600" />
              )}
              <div
                className="absolute inset-0 bg-black"
                style={{ opacity: props.overlayIntensity || 0.4 }}
              />
              <div className={`relative h-full flex flex-col justify-center items-center text-white p-8`}>
                <h1 className="text-4xl font-bold mb-4 hover:outline hover:outline-2 hover:outline-white/50 cursor-pointer rounded px-2"
                    title="Click section and edit in properties panel">
                  {props.title || 'Carousel Title'}
                </h1>
                <p className="text-xl mb-6 hover:outline hover:outline-2 hover:outline-white/50 cursor-pointer rounded px-2"
                   title="Click section and edit in properties panel">
                  {props.subtitle || 'Carousel Subtitle'}
                </p>
                {props.showPrimaryButton !== false && props.buttonText && (
                  <button className="px-6 py-3 bg-white text-blue-600 rounded-lg font-medium">
                    {props.buttonText}
                  </button>
                )}
              </div>
            </div>
          );
        }
        // Otherwise render as a testimonial carousel
        return (
          <div className="p-8">
            {props.title && (
              <h2 className="text-3xl font-bold text-center mb-8 hover:outline hover:outline-2 hover:outline-gray-400 cursor-pointer rounded px-2 inline-block"
                  title="Click section and edit in properties panel">
                {props.title}
              </h2>
            )}
            <div className="max-w-2xl mx-auto">
              <div className="bg-gray-50 p-6 rounded-lg">
                <p className="text-lg italic mb-4">
                  "{props.items?.[0]?.quote || 'Customer testimonial here'}"
                </p>
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-gray-300 rounded-full mr-4" />
                  <div>
                    <p className="font-semibold">{props.items?.[0]?.author || 'Customer Name'}</p>
                    <p className="text-sm text-gray-600">{props.items?.[0]?.role || 'Role'}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 'gallery':
      case 'content_grid': // Map database type to grid rendering
        const gridItems = props.items || props.images || [1, 2, 3, 4, 5, 6, 7, 8];
        return (
          <div className="p-8">
            {props.title && (
              <h2 className="text-3xl font-bold text-center mb-8 hover:outline hover:outline-2 hover:outline-gray-400 cursor-pointer rounded px-2 inline-block"
                  title="Click section and edit in properties panel">
                {props.title}
              </h2>
            )}
            <div className="grid grid-cols-4 gap-4">
              {gridItems.map((item: any, i: number) => (
                <div key={i} className="aspect-square bg-gray-200 rounded-lg overflow-hidden">
                  {typeof item === 'string' ? (
                    <img src={item} alt="" className="w-full h-full object-cover" />
                  ) : item.image ? (
                    <img src={item.image} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-400">
                      <span className="text-4xl">📷</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        );

      case 'stats':
        return (
          <div className="p-8 bg-gray-50">
            <div className="grid grid-cols-4 gap-6 text-center">
              {props.items?.map((item: any, i: number) => (
                <div key={i}>
                  <div className="text-3xl font-bold text-blue-600">{item.number}</div>
                  <div className="text-gray-600">{item.label}</div>
                </div>
              ))}
            </div>
          </div>
        );

      case 'pricing':
        return (
          <div className="p-8">
            {props.title && (
              <h2 className="text-3xl font-bold text-center mb-8 hover:outline hover:outline-2 hover:outline-gray-400 cursor-pointer rounded px-2 inline-block"
                  title="Click section and edit in properties panel">
                {props.title}
              </h2>
            )}
            <div className="grid grid-cols-3 gap-6">
              {props.plans?.map((plan: any, i: number) => (
                <div
                  key={i}
                  className={`border rounded-lg p-6 ${
                    plan.highlighted ? 'border-blue-500 shadow-lg' : 'border-gray-200'
                  }`}
                >
                  <h3 className="text-xl font-semibold mb-2">{plan.name}</h3>
                  <div className="text-3xl font-bold mb-4">
                    {plan.price}
                    <span className="text-sm text-gray-600">/{plan.period}</span>
                  </div>
                  <ul className="space-y-2 mb-6">
                    {plan.features?.map((feature: string, j: number) => (
                      <li key={j} className="text-gray-600">✓ {feature}</li>
                    ))}
                  </ul>
                  <button
                    className={`w-full py-2 rounded-lg font-medium ${
                      plan.highlighted
                        ? 'bg-blue-500 text-white'
                        : 'bg-gray-100 text-gray-700'
                    }`}
                  >
                    {plan.buttonText}
                  </button>
                </div>
              ))}
            </div>
          </div>
        );

      case 'quick_actions':
        const columns = props.columns || 4;
        const gridClass = columns === 2 ? 'grid-cols-2' :
                         columns === 3 ? 'grid-cols-2 md:grid-cols-3' :
                         columns === 6 ? 'grid-cols-2 md:grid-cols-3 lg:grid-cols-6' :
                         'grid-cols-2 md:grid-cols-4';

        const defaultActions = [
          { icon: '📚', label: 'Study Materials', link: '/materials' },
          { icon: '📝', label: 'Mock Tests', link: '/tests' },
          { icon: '📊', label: 'Analytics', link: '/analytics' },
          { icon: '💬', label: 'Discussions', link: '/discussions' }
        ];

        const actions = props.actions && props.actions.length > 0 ? props.actions : defaultActions;

        return (
          <div className="p-8 bg-gray-50">
            {props.title && (
              <h2 className="text-3xl font-bold text-center mb-4 hover:outline hover:outline-2 hover:outline-gray-400 cursor-pointer rounded px-2 inline-block"
                  title="Click section and edit in properties panel">
                {props.title}
              </h2>
            )}
            {props.subtitle && (
              <p className="text-center text-gray-600 mb-8">{props.subtitle}</p>
            )}
            <div className={`grid ${gridClass} gap-4 max-w-6xl mx-auto`}>
              {actions.map((action: any, i: number) => (
                <a
                  key={i}
                  href={action.link || '#'}
                  className="p-6 bg-white rounded-lg shadow hover:shadow-lg transition-all hover:scale-105 text-center block"
                  onClick={(e) => {
                    e.preventDefault(); // Prevent navigation in editor
                    console.log('Quick action clicked:', action.label);
                  }}
                >
                  <div className="text-3xl mb-2">{action.icon || '⚡'}</div>
                  <div className="font-medium text-gray-800">{action.label || 'Action'}</div>
                </a>
              ))}
            </div>
          </div>
        );

      case 'announcement':
        return (
          <div className="p-6 bg-blue-50 border-l-4 border-blue-500">
            <div className="flex items-start">
              <div className="text-2xl mr-3">📢</div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-blue-900 mb-2">
                  {props.title || 'Important Announcement'}
                </h3>
                <p className="text-blue-800">
                  {props.message || props.subtitle || 'Stay tuned for important updates.'}
                </p>
                {props.buttonText && (
                  <button className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">
                    {props.buttonText}
                  </button>
                )}
              </div>
            </div>
          </div>
        );

      case 'text':
        // Support both old single content and new blocks format
        if (props.blocks && props.blocks.length > 0) {
          return (
            <div className="p-8 space-y-4">
              {props.blocks.map((block: any, i: number) => {
                const Tag = block.type || 'p';
                const style: any = {
                  fontSize: `${block.fontSize || 16}px`,
                  fontWeight: block.fontWeight || 'normal',
                  textAlign: block.textAlign || 'left',
                  color: block.color || '#000000',
                  fontFamily: block.fontFamily === 'serif' ? 'Georgia, serif' :
                             block.fontFamily === 'sans-serif' ? 'Arial, sans-serif' :
                             block.fontFamily === 'monospace' ? 'Courier New, monospace' :
                             'inherit',
                  fontStyle: block.italic ? 'italic' : 'normal',
                  textDecoration: block.underline ? 'underline' : 'none'
                };

                // Apply bold with fontWeight override if explicitly set
                if (block.bold && block.fontWeight === 'normal') {
                  style.fontWeight = 'bold';
                }

                // Add proper margins for different elements
                if (block.type === 'h1') {
                  style.marginTop = '24px';
                  style.marginBottom = '16px';
                } else if (block.type === 'h2') {
                  style.marginTop = '20px';
                  style.marginBottom = '12px';
                } else if (block.type === 'h3') {
                  style.marginTop = '16px';
                  style.marginBottom = '8px';
                } else if (block.type === 'quote') {
                  style.borderLeft = '4px solid #e5e7eb';
                  style.paddingLeft = '16px';
                  style.fontStyle = 'italic';
                  style.color = '#6b7280';
                }

                return React.createElement(
                  Tag,
                  {
                    key: i,
                    style,
                    className: 'hover:outline hover:outline-2 hover:outline-gray-300 cursor-pointer rounded px-2',
                    title: 'Click section and edit in properties panel'
                  },
                  block.content || `Enter ${block.type === 'h1' ? 'heading' : block.type === 'p' ? 'paragraph' : block.type} text...`
                );
              })}
            </div>
          );
        }

        // Fallback to old format
        return (
          <div className="p-8">
            <div
              className="prose prose-lg max-w-none"
              style={{
                textAlign: props.textAlign || 'left',
                fontSize: props.fontSize === 'small' ? '14px' :
                         props.fontSize === 'large' ? '20px' : '16px'
              }}
            >
              <p className="hover:outline hover:outline-2 hover:outline-gray-300 cursor-pointer rounded px-2"
                 title="Click section and edit in properties panel">
                {props.content || 'Enter your text here. Click to edit.'}
              </p>
            </div>
          </div>
        );

      case 'image':
        return (
          <div className="p-8">
            <div className="max-w-4xl mx-auto">
              {props.src ? (
                <img
                  src={props.src}
                  alt={props.alt || 'Image'}
                  className="w-full h-auto rounded-lg shadow-md"
                  style={{
                    maxWidth: props.width || '100%',
                    height: props.height || 'auto'
                  }}
                />
              ) : (
                <div className="bg-gray-200 rounded-lg flex items-center justify-center"
                     style={{ height: props.height || '400px' }}>
                  <div className="text-center text-gray-500">
                    <Image className="w-16 h-16 mx-auto mb-2" />
                    <p>Click to upload image</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        );

      case 'button':
        return (
          <div className="p-8 text-center">
            <a
              href={props.link || '#'}
              className={`inline-block px-6 py-3 rounded-lg font-medium transition-all hover:scale-105 ${
                props.style === 'secondary' ? 'bg-gray-200 text-gray-800 hover:bg-gray-300' :
                props.style === 'outline' ? 'border-2 border-blue-500 text-blue-500 hover:bg-blue-50' :
                'bg-blue-500 text-white hover:bg-blue-600'
              } ${
                props.size === 'small' ? 'text-sm px-4 py-2' :
                props.size === 'large' ? 'text-lg px-8 py-4' :
                ''
              }`}
              onClick={(e) => e.preventDefault()}
            >
              {props.text || 'Click Me'}
            </a>
          </div>
        );

      case 'video':
        return (
          <div className="p-8">
            <div className="max-w-4xl mx-auto">
              {props.url ? (
                <video
                  src={props.url}
                  controls={props.controls !== false}
                  autoPlay={props.autoplay}
                  loop={props.loop}
                  muted={props.muted || props.autoplay}
                  className="w-full rounded-lg shadow-md"
                />
              ) : (
                <div className="bg-gray-200 rounded-lg flex items-center justify-center h-96">
                  <div className="text-center text-gray-500">
                    <Video className="w-16 h-16 mx-auto mb-2" />
                    <p>Add video URL</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        );

      case 'spacer':
        return (
          <div
            style={{ height: props.height || '50px' }}
            className="relative group"
          >
            <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
              <div className="bg-gray-800 text-white px-2 py-1 rounded text-xs">
                Spacer: {props.height || '50px'}
              </div>
            </div>
          </div>
        );

      case 'button_grid':
        // Render Button Grid sections
        return (
          <PremiumButtonGrid
            title={props.title}
            subtitle={props.subtitle}
            titleAlign={props.titleAlign}
            titleSize={props.titleSize}
            titleWeight={props.titleWeight}
            subtitleSize={props.subtitleSize}
            titleGap={props.titleGap}
            buttons={props.buttons || []}
            variant={props.variant as 'default' | 'rounded' | 'card' | 'gradient' | 'horizontal'}
            columns={props.columns || 2}
            gridSize={props.gridSize || 4}
            gap={props.gap || 16}
            buttonHeight={styles?.buttonHeight || '120px'}
            borderRadius={styles?.borderRadius || '12px'}
            shadowLevel={styles?.shadowLevel as 'none' | 'sm' | 'md' | 'lg' || 'md'}
            styles={styles}
          />
        );

      case 'text_card':
        return (
          <TextCard
            heading={props.heading}
            headingFont={props.headingFont}
            headingAlign={props.headingAlign}
            bodyText={props.bodyText}
            bodyFont={props.bodyFont}
            borderRadius={props.borderRadius}
            shadowLevel={props.shadowLevel}
          />
        );

      default:
        console.warn(`Unknown section type: "${type}" for section ${section.id}`);
        return (
          <div className="p-8 bg-gray-50">
            <div className="text-center text-gray-500">
              <Settings className="w-12 h-12 mx-auto mb-2" />
              <p>{section.title}</p>
              <p className="text-sm">Type: {type || 'undefined'}</p>
              <p className="text-xs text-red-500">Section type not recognized</p>
            </div>
          </div>
        );
    }
  };

  // Don't show hidden sections in preview modes
  if (!section.is_visible && device !== 'desktop') {
    return null;
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`relative group ${
        isSelected ? 'ring-2 ring-blue-500' : ''
      } ${!section.is_visible ? 'opacity-50' : ''}`}
      onClick={onSelect}
    >
      {/* Section Controls */}
      <div className={`absolute top-2 right-2 z-10 flex items-center gap-1 bg-white rounded-lg shadow-lg p-1 transition-opacity ${
        isSelected ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
      }`}>
        {/* Drag Handle */}
        <button
          className="p-1.5 hover:bg-gray-100 rounded cursor-move"
          {...attributes}
          {...listeners}
          title="Drag to reorder"
        >
          <GripVertical className="w-4 h-4 text-gray-600" />
        </button>

        {/* Move Up */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleMoveUp();
          }}
          disabled={index === 0}
          className="p-1.5 hover:bg-gray-100 rounded disabled:opacity-50"
          title="Move up"
        >
          <ChevronUp className="w-4 h-4 text-gray-600" />
        </button>

        {/* Move Down */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleMoveDown();
          }}
          className="p-1.5 hover:bg-gray-100 rounded"
          title="Move down"
        >
          <ChevronDown className="w-4 h-4 text-gray-600" />
        </button>

        <div className="w-px h-5 bg-gray-300" />

        {/* Visibility Toggle */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            toggleSectionVisibility(section.id);
          }}
          className="p-1.5 hover:bg-gray-100 rounded"
          title={section.is_visible ? 'Hide section' : 'Show section'}
        >
          {section.is_visible ? (
            <Eye className="w-4 h-4 text-gray-600" />
          ) : (
            <EyeOff className="w-4 h-4 text-gray-600" />
          )}
        </button>

        {/* Duplicate */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            duplicateSection(section.id);
          }}
          className="p-1.5 hover:bg-gray-100 rounded"
          title="Duplicate section"
        >
          <Copy className="w-4 h-4 text-gray-600" />
        </button>

        {/* Delete */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            if (confirm('Delete this section?')) {
              deleteSection(section.id);
            }
          }}
          className="p-1.5 hover:bg-red-100 rounded"
          title="Delete section"
        >
          <Trash2 className="w-4 h-4 text-red-600" />
        </button>
      </div>

      {/* Section Label */}
      <div className={`absolute top-2 left-2 z-10 bg-white rounded px-2 py-1 text-xs font-medium shadow transition-opacity ${
        isSelected ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
      }`}>
        {section.title}
      </div>

      {/* Edit Hint */}
      {!isSelected && (
        <div className={`absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-10
          bg-black/70 text-white rounded-lg px-4 py-2 text-sm font-medium pointer-events-none
          opacity-0 group-hover:opacity-100 transition-opacity`}>
          Click to edit this section
        </div>
      )}

      {/* Section Content */}
      <div
        className="relative overflow-x-hidden transition-all duration-300"
        style={getStyleFromValues(section.styles || {})}
      >
        {renderSectionContent()}
      </div>
    </div>
  );
};